import React, { useState, useEffect, useCallback } from 'react';

const testimonials = [
  { initials: 'PR', name: 'Priya Ramesh', meta: 'English · Bangalore', rating: 5, bg: 'rgba(15,244,198,0.15)', color: '#0ff4c6', quote: 'In just 8 weeks my spoken English improved dramatically. The live classes kept me accountable and the trainers are incredibly supportive.' },
  { initials: 'AK', name: 'Arjun Kapoor', meta: 'English · Delhi', rating: 5, bg: 'rgba(251,191,36,0.15)', color: '#fbbf24', quote: 'I had tried many apps but nothing clicked until RSCourses. The real-life conversation practice is what makes the difference.' },
  { initials: 'SM', name: 'Sneha Mehra', meta: 'French · Mumbai', rating: 5, bg: 'rgba(244,114,182,0.15)', color: '#f472b6', quote: 'The French course is exceptional. My pronunciation improved so much that native speakers actually complimented my accent.' },
  { initials: 'RV', name: 'Rohit Verma', meta: 'Korean · Hyderabad', rating: 5, bg: 'rgba(99,179,237,0.15)', color: '#63b3ed', quote: 'Korean was a dream and RSCourses made it real. I was reading Hangul within two weeks. The free demo convinced me instantly.' },
  { initials: 'DG', name: 'Divya Gupta', meta: 'English · Pune', rating: 4, bg: 'rgba(167,139,250,0.15)', color: '#a78bfa', quote: 'Affordable, structured and genuinely effective. I cracked my job interview after just 6 weeks of the English speaking course.' },
  { initials: 'NS', name: 'Nikhil Singh', meta: 'English + French · Jaipur', rating: 5, bg: 'rgba(52,211,153,0.15)', color: '#34d399', quote: 'Started with the free demo and never looked back. Now on my second course. The 30% discount on enrollment is a brilliant deal.' },
];

export default function Testimonials() {
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [screen, setScreen] = useState('desktop');

  const total = testimonials.length;

  // ✅ screen detection
  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      if (w < 640) setScreen('mobile');
      else if (w < 1024) setScreen('tablet');
      else setScreen('desktop');
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ✅ dynamic visible cards
  const visible = screen === 'mobile' ? 1 : screen === 'tablet' ? 2 : 3;

  const go = useCallback((dir) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrent(prev => (prev + dir + total) % total);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating, total]);

  useEffect(() => {
    const id = setInterval(() => go(1), 4500);
    return () => clearInterval(id);
  }, [go]);

  const getVisible = () => {
    return Array.from({ length: visible }).map((_, i) =>
      testimonials[(current + i) % total]
    );
  };

  return (
    <section
      id="testimonials"
      style={{
        padding: screen === 'mobile' ? '3rem 1rem' : '5rem 2rem',
        background: 'var(--bg)',
        overflow: 'hidden',
      }}
    >
      <div style={{ maxWidth: '1120px', margin: '0 auto' }}>

        {/* Header */}
        <div
          style={{
            display: 'flex',
            flexDirection: screen === 'mobile' ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: screen === 'mobile' ? 'flex-start' : 'flex-end',
            marginBottom: '2.5rem',
            gap: '1rem',
          }}
        >
          <div>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: '#0ff4c6',
              marginBottom: '10px',
              fontFamily: "'Syne', sans-serif"
            }}>
              Student stories
            </div>

            <h2 style={{
              fontFamily: "'Syne', sans-serif",
              fontSize: 'clamp(26px, 5vw, 46px)',
              fontWeight: '800',
              letterSpacing: '-1px',
              color: '#f0f4ff'
            }}>
              What our students <span style={{ color: '#0ff4c6' }}>say</span>
            </h2>
          </div>

          {/* Controls */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            alignItems: 'center'
          }}>
            {testimonials.map((_, i) => (
              <button key={i} onClick={() => setCurrent(i)} style={{
                width: i === current ? '24px' : '8px',
                height: '8px',
                borderRadius: '999px',
                background: i === current ? '#0ff4c6' : 'rgba(99,179,237,0.2)',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s',
              }} />
            ))}

            <div style={{ display: 'flex', gap: '8px' }}>
              {[{ dir: -1, label: '←' }, { dir: 1, label: '→' }].map(btn => (
                <button
                  key={btn.dir}
                  onClick={() => go(btn.dir)}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'var(--surface)',
                    border: '1px solid rgba(99,179,237,0.2)',
                    color: '#f0f4ff',
                    cursor: 'pointer',
                  }}
                >
                  {btn.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${visible}, 1fr)`,
            gap: '16px',
          }}
        >
          {getVisible().map((t, i) => (
            <div key={`${current}-${i}`} style={{
              background: 'var(--surface)',
              border: `1px solid ${i === 0 ? 'rgba(15,244,198,0.25)' : 'rgba(99,179,237,0.10)'}`,
              borderRadius: '24px',
              padding: screen === 'mobile' ? '1.2rem' : '1.75rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              opacity: i === 0 ? 1 : i === 1 ? 0.9 : 0.75,
              transform: `scale(${i === 0 ? 1 : i === 1 ? 0.98 : 0.95})`,
              transition: 'all 0.5s cubic-bezier(0.4,0,0.2,1)',
              animation: 'fadeSlideUp 0.5s ease forwards',
              animationDelay: `${i * 0.08}s`,
            }}>
              
              {/* Quote */}
              <div style={{
                fontSize: screen === 'mobile' ? '32px' : '48px',
                color: t.color,
                opacity: 0.3,
                marginBottom: '-8px'
              }}>
                "
              </div>

              {/* ⭐ Stars (RESTORED) */}
              <div style={{ display: 'flex', gap: '3px' }}>
                {[1,2,3,4,5].map(s => (
                  <span key={s} style={{
                    fontSize: screen === 'mobile' ? '12px' : '14px',
                    color: s <= t.rating ? '#fbbf24' : 'rgba(251,191,36,0.2)'
                  }}>
                    ★
                  </span>
                ))}
              </div>

              {/* Quote text */}
              <p style={{
                fontSize: '14px',
                color: '#94a3c4',
                lineHeight: 1.8,
                fontStyle: 'italic',
                fontWeight: '300',
                flex: 1
              }}>
                "{t.quote}"
              </p>

              {/* User */}
              <div style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
                paddingTop: '0.75rem',
                borderTop: '1px solid rgba(99,179,237,0.08)'
              }}>
                <div style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '50%',
                  background: t.bg,
                  color: t.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '13px',
                  fontWeight: '700'
                }}>
                  {t.initials}
                </div>

                <div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#f0f4ff'
                  }}>
                    {t.name}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#4a5878'
                  }}>
                    {t.meta}
                  </div>
                </div>
              </div>

            </div>
          ))}
        </div>

      </div>
    </section>
  );
}